/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Sidebar, tituloBase } from "../../../../shared";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import { Tooltip } from "primereact/tooltip";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  Boton,
  Titulo,
  Formulario,
  CampoInformacion,
  TablaFiltrable
} from "../../../../shared";

const SEARCH_FIELDS_CALIBRACIONES = ["_laboratorio", "notas"];

export default function Instrumento() {
  const [loader, setLoader] = useState(true);
  const [instrumento, setInstrumento] = useState();
  const [calibraciones, setCalibraciones] = useState([]);
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formEditarKey, setFormEditarKey] = useState(0);
  const [loaderInstructivo, setLoaderInstructivo] = useState(false);
  const [sideBarCalibracion, setSideBarCalibracion] = useState(false);
  const [loaderCalibracion, setLoaderCalibracion] = useState(false);
  const [formCalibracionKey, setFormCalibracionKey] = useState(0);
  const [descargandoCertificado, setDescargandoCertificado] = useState(null);

  const toast = useRef(null);
  const { instrumentoId } = useParams();

  const camposEditables = [
    { campo: "instrumento", etiqueta: "Tipo de instrumento", requerido: true },
    { campo: "marca", etiqueta: "Marca", requerido: true },
    { campo: "modelo", etiqueta: "Modelo", requerido: true },
    { campo: "numeroSerie", etiqueta: "Número de serie" },
    { campo: "caracteristicas", etiqueta: "Características", tipo: 11 },
    {
      campo: "frecuenciaCalibracionMeses",
      etiqueta: "Frecuencia de calibración (meses)",
      tipo: 4
    },
    { campo: "activo", etiqueta: "Activo", tipo: 6 }
  ];

  function guardarEdicion(contenidoCampos) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/instrumental/${instrumentoId}`,
      contenidoCampos,
      () => {
        setInstrumento((prev) => ({ ...prev, ...contenidoCampos }));
        setSideBarEditar(false);
        setLoaderEditar(false);
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al guardar",
          sticky: true
        });
      }
    );
  }

  function getEditarFormConfig() {
    return {
      secciones: [
        {
          campos: camposEditables.map((c) => ({
            nombre: c.campo,
            tipoitemformularioid: c.tipo || 1,
            etiqueta: c.etiqueta,
            requerido: c.requerido || false,
            respuesta: instrumento[c.campo] ?? ""
          }))
        }
      ],
      botones: [
        {
          etiqueta: "Guardar cambios",
          callback: guardarEdicion,
          validar: true
        }
      ]
    };
  }

  function cargarInstrumento() {
    accessAPI(
      "GET",
      `admin/instrumental/${instrumentoId}`,
      null,
      (respuesta) => {
        setInstrumento(respuesta);
        setCalibraciones(respuesta.Calibracion);
        document.title = `${respuesta.instrumento} - ${respuesta.marca} ${respuesta.modelo}`;
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar instrumento",
          sticky: true
        });
        setLoader(false);
      }
    );
  }

  function descargarInstructivo() {
    setLoaderInstructivo(true);
    downloadFile(
      `admin/archivo/${instrumento.instructivoId}`,
      `Instructivo - ${instrumento.marca} ${instrumento.modelo}`,
      () => setLoaderInstructivo(false),
      (respuesta) => {
        setLoaderInstructivo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar instructivo",
          sticky: true
        });
      }
    );
  }

  function crearCalibracion(camposFormulario, archivo) {
    setLoaderCalibracion(true);
    accessAPI(
      "POST",
      `admin/instrumental/calibracion/${instrumento.id}`,
      camposFormulario,
      (respuesta) => {
        setCalibraciones((current) => [respuesta, ...current]);
        setSideBarCalibracion(false);
        setLoaderCalibracion(false);
      },
      (respuesta) => {
        setLoaderCalibracion(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar calibración",
          sticky: true
        });
      },
      archivo
    );
  }

  function getCalibracionFormConfig() {
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "fecha",
              tipoitemformularioid: 7,
              etiqueta: "Fecha",
              requerido: true
            },
            {
              nombre: "laboratorioId",
              tipoitemformularioid: 5,
              etiqueta: "Laboratorio de calibración",
              opciones: "admin/laboratorio?escalibracion=true",
              requerido: true,
              sinOpcionInicial: true
            },
            {
              nombre: "certificado",
              tipoitemformularioid: 10,
              etiqueta: "Certificado de calibración",
              requerido: true
            },
            {
              nombre: "notas",
              tipoitemformularioid: 11,
              etiqueta: "Notas"
            }
          ]
        }
      ],
      botones: [
        {
          etiqueta: "Agregar calibración",
          callback: crearCalibracion,
          validar: true
        }
      ]
    };
  }

  function descargarCertificado(calibracion) {
    if (descargandoCertificado) return;
    setDescargandoCertificado(calibracion.id);
    downloadFile(
      `admin/archivo/${calibracion.archivoId}`,
      `Certificado calibracion - ${instrumento.marca} ${instrumento.modelo} - ${formatFecha(calibracion.fecha)}`,
      () => setDescargandoCertificado(null),
      (respuesta) => {
        setDescargandoCertificado(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar certificado",
          sticky: true
        });
      }
    );
  }

  useEffect(() => {
    cargarInstrumento();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const datosCalibraciones = (calibraciones || []).map((c) => ({
    ...c,
    _laboratorio:
      c.laboratorioProveedor?.nombrefantasia ||
      c.laboratorioProveedor?.razonsocial ||
      ""
  }));

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar instrumento</Titulo>
        {instrumento && (
          <Formulario
            key={formEditarKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        )}
      </Sidebar>

      <Sidebar
        visible={sideBarCalibracion}
        onHide={() => setSideBarCalibracion(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nueva calibración</Titulo>
        {instrumento && (
          <Formulario
            key={formCalibracionKey}
            config={getCalibracionFormConfig()}
            loader={loaderCalibracion}
          />
        )}
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="20rem" height="2rem" />
            </div>
          </div>
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && instrumento && (
        <div>
          <Titulo
            urlVolver="/instrumental"
            etiquetas={[
              ...(!instrumento.activo
                ? [{ texto: "No activo", error: true }]
                : []),
              ...(instrumento.activo &&
              instrumento.diasParaCalibracion !== undefined
                ? [
                    instrumento.diasParaCalibracion <= 0
                      ? {
                          texto: `Calibración vencida (${Math.abs(instrumento.diasParaCalibracion)} días)`,
                          error: true
                        }
                      : {
                          texto: `Próxima calibración en ${instrumento.diasParaCalibracion} días`,
                          alerta: true
                        }
                  ]
                : [])
            ]}
          >
            {instrumento.instrumento} - {instrumento.marca}{" "}
            {instrumento.modelo}
          </Titulo>

          <Card>
            <Titulo
              chico
              botones={
                <Boton
                  texto
                  negro
                  callback={() => {
                    setFormEditarKey((k) => k + 1);
                    setSideBarEditar(true);
                  }}
                >
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Tipo de instrumento">
                {instrumento.instrumento}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Marca">
                {instrumento.marca}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Modelo">
                {instrumento.modelo}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Número de serie">
                {instrumento.numeroSerie}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de alta">
                {formatFecha(instrumento.fechaalta)}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Frecuencia de calibración">
                {instrumento.frecuenciaCalibracionMeses
                  ? `${instrumento.frecuenciaCalibracionMeses} meses`
                  : "-"}
              </CampoInformacion>
            </div>
            {instrumento.caracteristicas && (
              <>
                <Divider />
                <div className="grid">
                  <CampoInformacion
                    nombreCampo="Características"
                    anchoCompleto
                  >
                    <span css={{ whiteSpace: "pre-line" }}>
                      {instrumento.caracteristicas}
                    </span>
                  </CampoInformacion>
                </div>
              </>
            )}
            {instrumento.instructivoId && (
              <>
                <Divider />
                <Boton
                  loader={loaderInstructivo}
                  callback={descargarInstructivo}
                >
                  <i
                    className="pi pi-download"
                    css={{ marginRight: "0.5em" }}
                  />
                  Descargar instructivo
                </Boton>
              </>
            )}
          </Card>

          <Card css={{ marginTop: "1em" }}>
            <Titulo
              chico
              botones={
                <Boton
                  texto
                  negro
                  callback={() => {
                    setFormCalibracionKey((k) => k + 1);
                    setSideBarCalibracion(true);
                  }}
                >
                  <i className="pi pi-plus" />
                </Boton>
              }
            >
              Calibraciones
            </Titulo>
            <TablaFiltrable
              key={`calibraciones-${descargandoCertificado}`}
              value={datosCalibraciones}
              searchFields={SEARCH_FIELDS_CALIBRACIONES}
              anchoBusqueda="33%"
              nombreArchivo="calibraciones"
              sortField="fecha"
              sortOrder={-1}
              emptyMessage="No hay calibraciones registradas"
            >
              <Column
                header="Fecha"
                body={(rowData) => formatFecha(rowData.fecha)}
                field="fecha"
                style={{ width: "130px" }}
              />
              <Column
                header="Laboratorio"
                body={(rowData) =>
                  rowData.laboratorioProveedor?.nombrefantasia ||
                  rowData.laboratorioProveedor?.razonsocial
                }
              />
              <Column
                body={(rowData) => {
                  if (!rowData.notas) return null;
                  const tooltipId = `notas-calibracion-${rowData.id}`;
                  return (
                    <>
                      <Tooltip
                        target={`#${tooltipId}`}
                        content={rowData.notas}
                        position="top"
                      />
                      <i
                        id={tooltipId}
                        className="pi pi-info-circle"
                        style={{
                          color: "var(--amarillo)",
                          fontSize: "1.2rem",
                          cursor: "pointer"
                        }}
                      />
                    </>
                  );
                }}
                style={{ width: "60px", textAlign: "center" }}
              />
              <Column
                body={(rowData) => {
                  if (!rowData.archivoId) return null;
                  const descargando = descargandoCertificado === rowData.id;
                  return (
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5em"
                      }}
                    >
                      {descargando && (
                        <i className="pi pi-spin pi-spinner" />
                      )}
                      <Boton
                        texto
                        negro
                        chico
                        callback={() => descargarCertificado(rowData)}
                      >
                        <i className="pi pi-download" />
                      </Boton>
                    </div>
                  );
                }}
                style={{ width: "60px", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
